import { useState, useEffect, useCallback } from 'react';
import type { SerializedAvailabilityBlock } from '@/components/operator/calendar/FullCalendarWrapper';
import { apiFetch as baseApiFetch } from '@/lib/operator-api/base-client';

async function apiFetch<T>(path: string): Promise<T> {
  const res = await baseApiFetch(path);
  if (!res.ok) throw new Error(`API error ${res.status}`);
  const json = await res.json();
  return json.data ?? json;
}

export function useStaffData(operatorExists: boolean, businessId: string | null) {
  const [staff, setStaff] = useState<any[]>([]);
  const [staffLoading, setStaffLoading] = useState(false);

  useEffect(() => {
    if (!operatorExists || !businessId) return;
    setStaffLoading(true);
    apiFetch<any[]>(`/api/operator/staff?businessId=${businessId}`)
      .then(setStaff)
      .catch(() => setStaff([]))
      .finally(() => setStaffLoading(false));
  }, [operatorExists, businessId]);

  return { staff, staffLoading };
}

export function useServicesData(operatorExists: boolean, businessId: string | null) {
  const [services, setServices] = useState<any[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  useEffect(() => {
    if (!operatorExists || !businessId) return;
    setServicesLoading(true);
    apiFetch<any[]>(`/api/operator/services?businessId=${businessId}`)
      .then(setServices)
      .catch(() => setServices([]))
      .finally(() => setServicesLoading(false));
  }, [operatorExists, businessId]);

  return { services, setServices, servicesLoading };
}

export function useBookingsData(
  operatorExists: boolean,
  businessId: string | null,
  selectedStaffIds: string[],
  selectedServiceIds: string[],
  services: any[],
  setServices: (services: any[]) => void
) {
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!operatorExists || !businessId) return;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ businessId, pageSize: '200' });
    if (selectedStaffIds.length > 0) params.set('staffIds', selectedStaffIds.join(','));
    if (selectedServiceIds.length > 0) params.set('serviceIds', selectedServiceIds.join(','));

    apiFetch<any[]>(`/api/operator/bookings?${params.toString()}`)
      .then(setBookings)
      .catch((e) => { setError(e.message); setBookings([]); })
      .finally(() => setLoading(false));
  }, [operatorExists, businessId, selectedStaffIds, selectedServiceIds]);

  return { bookings, loading, error };
}

export function useAvailabilityData(
  operatorExists: boolean,
  businessId: string | null,
  selectedStaffIds: string[],
  staff: any[]
) {
  const [availabilityBlocks, setAvailabilityBlocks] = useState<SerializedAvailabilityBlock[]>([]);
  const [showAvailability, setShowAvailability] = useState(true);

  useEffect(() => {
    if (!operatorExists || !businessId || selectedStaffIds.length === 0) return;

    // Fetch availability for each selected staff member
    Promise.all(
      selectedStaffIds.map((staffId) =>
        apiFetch<any[]>(`/api/operator/staff/${staffId}/availability`).catch(() => [])
      )
    )
      .then((results) => setAvailabilityBlocks(results.flat()))
      .catch(() => setAvailabilityBlocks([]));
  }, [operatorExists, businessId, selectedStaffIds]);

  const toggleAvailabilityVisibility = useCallback(() => {
    setShowAvailability((prev) => !prev);
  }, []);

  const saveAvailabilityBlock = useCallback(async (block: any) => {
    if (!businessId) return;
    await baseApiFetch(`/api/operator/staff/${block.staffId}/availability`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(block),
    });
  }, [businessId]);

  return { availabilityBlocks, showAvailability, toggleAvailabilityVisibility, saveAvailabilityBlock };
}

export function calculateDurationMinutes(start: string, end: string): number {
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return 0;
  return Math.round((endDate.getTime() - startDate.getTime()) / 60000);
}
